import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PhoneCall, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { name: 'Capabilities', path: '/capabilities' },
  { name: 'Pricing', path: '/pricing' },
  { name: 'Reporting', path: '/reporting' },
  { name: 'Contact', path: '/contact' },
];

const PublicNav = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav
      className="fixed top-0 w-full z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5"
      data-testid="public-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <PhoneCall size={26} className="text-[#FF3366] group-hover:scale-110 transition-transform" />
          <span
            className="text-xl font-semibold text-white tracking-tight"
            style={{ fontFamily: "'Clash Display', sans-serif" }}
          >
            My Quality
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className={`text-sm font-medium transition-colors ${
                location.pathname === l.path ? 'text-white' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid={`nav-link-${l.name.toLowerCase()}`}
            >
              {l.name}
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/login"
            className="rounded-full border border-white/20 text-white px-6 py-2 text-sm font-semibold hover:bg-white hover:text-black transition-all"
            data-testid="nav-login-btn"
          >
            Login
          </Link>
          <Link
            to="/login?tab=register"
            className="rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF8C00] text-white px-6 py-2 text-sm font-semibold hover:scale-105 transition-transform shadow-[0_0_20px_rgba(255,51,102,0.3)]"
            data-testid="nav-signup-btn"
          >
            Sign Up
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0F0F11] border-t border-white/5"
          >
            <div className="px-6 py-4 space-y-3">
              {navLinks.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  onClick={() => setMobileOpen(false)}
                  className="block text-zinc-300 hover:text-white py-2"
                >
                  {l.name}
                </Link>
              ))}
              <div className="flex gap-3 pt-3 border-t border-white/10">
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 text-center rounded-full border border-white/20 text-white px-4 py-2 text-sm font-semibold"
                >
                  Login
                </Link>
                <Link
                  to="/login?tab=register"
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 text-center rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF8C00] text-white px-4 py-2 text-sm font-semibold"
                >
                  Sign Up
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default PublicNav;
